import type { TFunction } from 'i18next'

import type { Order } from '../../utils/redux/ordersSlice'

export type OrderStatus = 'processing' | 'delivered'

export type OrderPlaylist = {
  _id: string
  title: string
  image?: string
}

type UnknownRecord = Record<string, unknown>

function asId(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    const s = String(value).trim()
    return s || ''
  }
  return ''
}

function asText(value: unknown): string {
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value).trim()
  }
  return ''
}

export function toLocaleFromLang(lang: string) {
  const code = String(lang || 'en').split('-')[0]
  if (code === 'uk') return 'uk-UA'
  if (code === 'fr') return 'fr-FR'
  if (code === 'ru') return 'ru-RU'
  return 'en-GB'
}

export function normalizeTs(ts: number) {
  return ts < 1e12 ? ts * 1000 : ts
}

export function formatDate(ts: number, locale = 'en-GB') {
  const d = new Date(normalizeTs(ts))
  if (Number.isNaN(d.getTime())) return ''
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d)
}

export function getOrderStatus(order: Order | UnknownRecord): OrderStatus {
  const st = String((order as UnknownRecord)?.status ?? '').toLowerCase()
  if (st === 'delivered') return 'delivered'
  if (st === 'processing') return 'processing'

  return extractOrderPlaylists(order).length > 0 ? 'delivered' : 'processing'
}

export function orderHasDeliveredPlaylists(order: Order | UnknownRecord) {
  return extractOrderPlaylists(order).length > 0
}

export function extractPlaylistId(order: Order | UnknownRecord): string | null {
  const data = order as UnknownRecord

  const directId =
    asId(data.playlistId) || asId(data.playlist_id) || asId(data.playlistID)
  if (directId) return directId

  const playlist = data.playlist
  if (playlist && typeof playlist === 'object') {
    const nested = playlist as UnknownRecord
    const nestedId = asId(nested._id) || asId(nested.id)
    if (nestedId) return nestedId
  }

  const extracted = extractOrderPlaylists(order)
  if (extracted.length > 0) return extracted[0]._id

  const playlistIds = data.playlistIds
  if (Array.isArray(playlistIds)) {
    for (const pid of playlistIds) {
      const id = asId(pid)
      if (id) return id
    }
  }

  return null
}

export function extractOrderPlaylists(order: Order | UnknownRecord): OrderPlaylist[] {
  const items = (order as UnknownRecord).playlists
  if (!Array.isArray(items)) return []

  const result: OrderPlaylist[] = []

  for (const item of items) {
    if (typeof item === 'string' || typeof item === 'number') {
      const id = asId(item)
      if (id) {
        result.push({ _id: id, title: id })
      }
      continue
    }

    if (item && typeof item === 'object') {
      const playlist = item as UnknownRecord
      const id = asId(playlist._id) || asId(playlist.id)
      if (!id) continue

      const title = asText(playlist.title) || asText(playlist.name) || id
      const image = asText(playlist.image) || undefined

      result.push({ _id: id, title, image })
    }
  }

  return result
}

function getPreferenceLabel(
  value: string,
  leftLabel: string,
  rightLabel: string,
): string {
  const numericValue = Number(value)

  if (Number.isNaN(numericValue)) {
    return ''
  }

  if (numericValue === 50) {
    return `${leftLabel} / ${rightLabel}`
  }

  if (numericValue > 50) {
    return `${leftLabel} ${numericValue}%`
  }

  return `${rightLabel} ${100 - numericValue}%`
}

function getOrderStyles(order: Order): string[] {
  return Array.isArray(order.styles)
    ? order.styles.map((style) => String(style).trim()).filter(Boolean)
    : []
}

function mapStyleToOrderStyleOption(style: string): string | null {
  const normalized = style.trim().toLowerCase()

  const map: Record<string, string> = {
    pop: 'pop',
    pop_music: 'pop',
    jazzblues: 'jazzBlues',
    jazz_blues: 'jazzBlues',
    jazz_music: 'jazzBlues',
    classical: 'classical',
    classic_music: 'classical',
    rock: 'rock',
    rock_music: 'rock',
    hiphoprnb: 'hiphopRnb',
    'hip-hop-rnb': 'hiphopRnb',
    hiphop_music: 'hiphopRnb',
    retro: 'retro',
    clubdeephouse: 'clubDeepHouse',
    club_deep_house: 'clubDeepHouse',
    electronic_music: 'clubDeepHouse',
    funksoul: 'funkSoul',
    funk_soul: 'funkSoul',
    loungechillout: 'loungeChillout',
    lounge_music: 'loungeChillout',
    bossanova: 'bossaNova',
    bossa_nova: 'bossaNova',
    latina: 'latina',
    italian: 'italian',
    france: 'france',
    ukrainian: 'ukrainian',
    georgian: 'georgian',
  }

  return map[normalized] ?? null
}

function fallbackStyleLabel(style: string): string {
  const normalized = style.trim().toLowerCase()
  const nativeFallback: Record<string, string> = {
    pop_music: 'Pop Music',
    lounge_music: 'Lounge/Chill Out',
    jazz_music: 'Jazz',
    classic_music: 'Classical Music',
    rock_music: 'Rock Music',
    hiphop_music: 'Hip-Hop/RnB',
    electronic_music: 'Club Music/Deep House/House',
  }

  if (nativeFallback[normalized]) return nativeFallback[normalized]

  return style
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (ch) => ch.toUpperCase())
}

function translateStyleChip(style: string, t: TFunction): string {
  const normalized = style.trim().toLowerCase()

  if (normalized.endsWith('_music')) {
    return t(normalized, {
      ns: 'common',
      defaultValue: fallbackStyleLabel(normalized),
    })
  }

  const mapped = mapStyleToOrderStyleOption(normalized)
  if (mapped) {
    return t(`create.styles.options.${mapped}`, {
      defaultValue: fallbackStyleLabel(style),
    })
  }

  return fallbackStyleLabel(style)
}

export function getOrderDetailChips(order: Order, t: TFunction): string[] {
  const styles = getOrderStyles(order)
  if (styles.length > 0) return styles.map((style) => translateStyleChip(style, t))

  return [
    t('details.clientsChip', { defaultValue: 'Client profile' }),
    t('details.preferencesChip', { defaultValue: 'Preferences' }),
  ]
}

export function getOrderPreferenceDetails(order: Order, t: TFunction): string[] {
  return [
    getPreferenceLabel(
      String(order.vocalToInstrumental ?? ''),
      t('details.vocal', { defaultValue: 'Vocal' }),
      t('details.instrumental', { defaultValue: 'Instrumental' }),
    ),
    getPreferenceLabel(
      String(order.internationalToLocal ?? ''),
      t('details.international', { defaultValue: 'International' }),
      t('details.local', { defaultValue: 'Local' }),
    ),
  ].filter(Boolean)
}

export function getOrderStatusLabel(order: Order, t: TFunction): string {
  return orderHasDeliveredPlaylists(order)
    ? t('status.delivered', { defaultValue: 'Order delivered' })
    : t('status.processing', { defaultValue: 'Processing your order' })
}
